// Commitment Tracker URL ↔ filter serialisation — FE-041.
//
// URL is the source of truth. Drill-through links from the dashboard
// (FE-031..FE-037) and from Account surfaces (FE-038..FE-040) all
// land here with a stable set of query keys; this helper parses them
// back into the typed ``CommitmentListFilters`` shape and serialises
// them again on every filter change.

import type {
  CommitmentListFilters,
  CommitmentSortKey,
  CommitmentStatus,
  CommitmentPriority,
} from "./types";


export const DEFAULT_COMMITMENT_LIMIT = 25;
export const DEFAULT_COMMITMENT_SORT_BY: CommitmentSortKey =
  "due_date_time";
export const DEFAULT_COMMITMENT_SORT_DIR: "asc" | "desc" = "asc";
export type CommitmentView = "list" | "board";
export const DEFAULT_COMMITMENT_VIEW: CommitmentView = "list";


/** Saved-filter / advanced-filter tree shape (mirrors the BE-052
 * validator's recursive ``{op, children, field, value}`` schema). */
export interface FilterLeaf {
  op:
    | "eq" | "neq" | "in" | "nin"
    | "lt" | "lte" | "gt" | "gte"
    | "contains" | "is_null" | "is_not_null";
  field: string;
  value?: unknown;
}


export interface FilterLogicalNode {
  op: "and" | "or";
  children: FilterTree[];
}


export type FilterTree = FilterLeaf | FilterLogicalNode;


const FILTER_MAX_BYTES = 8 * 1024;


/** Decode the ``?filter=`` query param into a tree object. Returns
 * ``undefined`` when missing / malformed (the BE will surface a
 * 400 on the same payload, so we don't double-throw on the
 * client). 8 KiB cap mirrors the BE limit. */
export function parseFilterTreeFromSearch(
  search: URLSearchParams,
): FilterTree | undefined {
  const raw = search.get("filter");
  if (!raw) return undefined;
  if (new TextEncoder().encode(raw).length > FILTER_MAX_BYTES) {
    return undefined;
  }
  try {
    const decoded = JSON.parse(raw) as unknown;
    if (decoded && typeof decoded === "object") {
      return decoded as FilterTree;
    }
  } catch {
    return undefined;
  }
  return undefined;
}


export function serializeFilterTree(tree: FilterTree | undefined): string | undefined {
  if (tree === undefined) return undefined;
  const encoded = JSON.stringify(tree);
  if (new TextEncoder().encode(encoded).length > FILTER_MAX_BYTES) {
    return undefined;
  }
  return encoded;
}


const COMMITMENT_TRACKER_PATH = "/dashboard/commitment-tracker";


const STATUSES: CommitmentStatus[] = [
  "backlog", "ready_for_work", "in_progress",
  "in_review", "done", "cancelled",
];


const PRIORITIES: CommitmentPriority[] = ["high", "medium", "low"];


const SORT_KEYS: CommitmentSortKey[] = [
  "title", "due_date_time", "priority", "status", "updated_at",
];


function readBool(value: string | null): boolean | undefined {
  if (value === "true") return true;
  if (value === "false") return false;
  return undefined;
}


function readEnum<T extends string>(
  value: string | null, allowed: readonly T[],
): T | undefined {
  if (value === null) return undefined;
  return (allowed as readonly string[]).includes(value)
    ? (value as T)
    : undefined;
}


export function parseCommitmentFiltersFromSearch(
  search: URLSearchParams,
): CommitmentListFilters {
  const filters: CommitmentListFilters = {};
  const search_ = search.get("search");
  if (search_ != null && search_ !== "") filters.search = search_;
  const status = readEnum(search.get("status"), STATUSES);
  if (status) filters.status = status;
  const priority = readEnum(search.get("priority"), PRIORITIES);
  if (priority) filters.priority = priority;
  const client = search.get("client_id") ?? search.get("account_id");
  if (client) filters.client_id = client;
  const planYear = search.get("plan_year_id");
  if (planYear) filters.plan_year_id = planYear;
  const assigned = search.get("assigned_to");
  if (assigned) filters.assigned_to = assigned;
  const category = search.get("category");
  if (category) filters.category = category;
  const overdue = readBool(search.get("overdue"));
  if (overdue !== undefined) filters.overdue = overdue;
  const isBlocked = readBool(search.get("is_blocked"));
  if (isBlocked !== undefined) filters.is_blocked = isBlocked;
  const hasAssignee = readBool(search.get("has_assignee"));
  if (hasAssignee !== undefined) filters.has_assignee = hasAssignee;
  const hasDue = readBool(search.get("has_due_date_time"));
  if (hasDue !== undefined) filters.has_due_date_time = hasDue;
  const onTime = readBool(search.get("on_time"));
  if (onTime !== undefined) filters.on_time = onTime;
  const snapshot = search.get("snapshot_date");
  if (snapshot) filters.snapshot_date = snapshot;
  const includeCancelled = readBool(search.get("include_cancelled"));
  if (includeCancelled === true) filters.include_cancelled = true;
  // FE/BE-043b — archive lifecycle + Private visibility filters.
  const archived = readBool(search.get("archived"));
  if (archived !== undefined) filters.archived = archived;
  const visibility = search.get("visibility");
  if (visibility === "private" || visibility === "team") {
    filters.visibility = visibility;
  }
  const source = search.get("source");
  if (source === "manual" || source === "playbook") {
    filters.source = source;
  }
  const playbookApplicationId = search.get("playbook_application_id");
  if (playbookApplicationId) {
    filters.playbook_application_id = playbookApplicationId;
  }
  return filters;
}


export function parseCommitmentSortFromSearch(
  search: URLSearchParams,
): { sortBy: CommitmentSortKey; sortDir: "asc" | "desc" } {
  const sortBy = readEnum(search.get("sort_by"), SORT_KEYS)
    ?? DEFAULT_COMMITMENT_SORT_BY;
  const sortDirRaw = (search.get("sort_dir") ?? "").toLowerCase();
  const sortDir =
    sortDirRaw === "asc" || sortDirRaw === "desc"
      ? (sortDirRaw as "asc" | "desc")
      : DEFAULT_COMMITMENT_SORT_DIR;
  return { sortBy, sortDir };
}


export function parseCommitmentOffsetFromSearch(
  search: URLSearchParams,
): number {
  const raw = Number(search.get("offset") ?? "0");
  return Number.isFinite(raw) && raw >= 0 ? Math.floor(raw) : 0;
}


// FE-042: ``view`` is the segmented-control selector. Default
// (and unknown values) fall back to ``list`` so dashboard
// drill-throughs that don't carry a ``view`` key keep landing on
// the list table — board mode is opt-in via ``?view=board``.
export function parseCommitmentViewFromSearch(
  search: URLSearchParams,
): CommitmentView {
  const raw = (search.get("view") ?? "").toLowerCase();
  return raw === "board" ? "board" : "list";
}


// Grouped list view: which status groups the user has collapsed.
// Persisted in the URL so refresh / deep-link doesn't re-expand
// huge buckets the user just collapsed. Comma-separated allow-list,
// e.g. ``?collapsed=backlog,done``. Unknown values are dropped.
export function parseCommitmentCollapsedFromSearch(
  search: URLSearchParams,
): CommitmentStatus[] {
  const raw = search.get("collapsed");
  if (!raw) return [];
  const out: CommitmentStatus[] = [];
  const seen = new Set<string>();
  for (const token of raw.split(",")) {
    const trimmed = token.trim();
    if (!trimmed || seen.has(trimmed)) continue;
    if ((STATUSES as readonly string[]).includes(trimmed)) {
      out.push(trimmed as CommitmentStatus);
      seen.add(trimmed);
    }
  }
  return out;
}


export function serializeCommitmentTrackerUrl(
  filters: CommitmentListFilters,
  sortBy: CommitmentSortKey,
  sortDir: "asc" | "desc",
  offset: number,
  view: CommitmentView = DEFAULT_COMMITMENT_VIEW,
  filterTree?: FilterTree,
  collapsed: CommitmentStatus[] = [],
): string {
  const sp = new URLSearchParams();
  appendString(sp, "search", filters.search);
  appendString(sp, "status", filters.status);
  appendString(sp, "priority", filters.priority);
  appendString(sp, "client_id", filters.client_id);
  appendString(sp, "plan_year_id", filters.plan_year_id);
  appendString(sp, "assigned_to", filters.assigned_to);
  appendString(sp, "category", filters.category);
  appendBool(sp, "overdue", filters.overdue);
  appendBool(sp, "is_blocked", filters.is_blocked);
  appendBool(sp, "has_assignee", filters.has_assignee);
  appendBool(sp, "has_due_date_time", filters.has_due_date_time);
  appendBool(sp, "on_time", filters.on_time);
  appendString(sp, "snapshot_date", filters.snapshot_date);
  // ``include_cancelled`` only surfaces on the URL when true — the
  // default (false) is the column-set default, so we don't pollute
  // the URL with ``include_cancelled=false``.
  if (filters.include_cancelled === true) {
    sp.append("include_cancelled", "true");
  }
  // FE/BE-043b — only emit ``archived`` and ``visibility`` when set.
  // Default tracker view is "active, no visibility filter", so the
  // canonical URL stays clean.
  if (filters.archived !== undefined) {
    sp.append("archived", filters.archived ? "true" : "false");
  }
  if (filters.visibility) {
    sp.append("visibility", filters.visibility);
  }
  if (filters.source) {
    sp.append("source", filters.source);
  }
  if (filters.playbook_application_id) {
    sp.append("playbook_application_id", filters.playbook_application_id);
  }
  if (sortBy !== DEFAULT_COMMITMENT_SORT_BY) sp.append("sort_by", sortBy);
  if (sortDir !== DEFAULT_COMMITMENT_SORT_DIR) {
    sp.append("sort_dir", sortDir);
  }
  if (offset > 0) sp.append("offset", String(offset));
  if (view !== DEFAULT_COMMITMENT_VIEW) sp.append("view", view);
  if (collapsed.length > 0) {
    // Serialise in canonical board order so the URL is stable
    // regardless of the order the user clicked the toggles.
    const ordered = STATUSES.filter((s) => collapsed.includes(s));
    if (ordered.length > 0) sp.append("collapsed", ordered.join(","));
  }
  if (filterTree !== undefined) {
    const encoded = serializeFilterTree(filterTree);
    if (encoded !== undefined) sp.append("filter", encoded);
  }
  const entries = Array.from(sp.entries()).sort(
    ([a], [b]) => (a < b ? -1 : a > b ? 1 : 0),
  );
  if (entries.length === 0) return COMMITMENT_TRACKER_PATH;
  const sorted = new URLSearchParams();
  for (const [k, v] of entries) sorted.append(k, v);
  return `${COMMITMENT_TRACKER_PATH}?${sorted.toString()}`;
}


function appendString(
  sp: URLSearchParams, key: string, value: string | undefined,
): void {
  if (value === undefined || value === null || value === "") return;
  sp.append(key, value);
}


function appendBool(
  sp: URLSearchParams, key: string, value: boolean | undefined,
): void {
  if (value === undefined) return;
  sp.append(key, value ? "true" : "false");
}
